import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SendHorizontal } from 'lucide-react';
import { 
    useContentSources, 
    useContentSource,
    useCreateContentSource, 
    useDeleteContentSource, 
    useAssignContentSource,
    useExportContentSource,
    useUpdateContentSource
} from '../hooks/useContentSources';
import { 
    ContentSource, 
    FilterState, 
    ContentStatus,
    CONTENT_STATUS_LABELS, 
    VISIBILITY_LABELS, 
    REVIEW_STATUS_LABELS,
    CONTENT_STATUS_COLORS,
    REVIEW_STATUS_COLORS,
    VISIBILITY_COLORS,
    SOURCE_LABELS
} from '../types/cms';
import { cmsUtils, contentSourceApi } from '../services/cmsApi';
import { useAuth } from '../context/AuthContext';
import { getSubordinateUsers, getUsers } from '../services/api';
import { User } from '../types';
import ContentSourceModal from '../components/ContentSourceModal';
import './CMSDashboard.css';

const CMSDashboard: React.FC = () => {
    const { authState } = useAuth();
    const user = authState.user;
    const navigate = useNavigate();
    
    // Auto-filter based on user role
    const [filters, setFilters] = useState<FilterState>({
        search: '',
        status: user?.role === 'reviewer' ? 'ethics_review_in_process' : '', // Auto-filter reviewers to see ready_for_review
        filter_tags: [],
        assigned_to_id: user?.role === 'reviewer' ? user.id : '', // Auto-filter for reviewers
        created_by_id: '',
        updated_by_id: '',
        visibility: '',
        review_status: '',
        sortBy: 'updated_at',
        sortOrder: 'DESC',
        page: 1,
        limit: 20
    });
    
    // Update filters when user role changes
    React.useEffect(() => {
        if (user) {
            if (user.role === 'reviewer') {
                // Reviewers see only assigned content sources that are ready for review
                setFilters(prev => ({ ...prev, assigned_to_id: user.id, status: 'ethics_review_in_process' }));
            } else if (user.role === 'editor' || user.role === 'editor-reviewer') {
                // Editors and editor-reviewers see content sources they created OR are assigned to
                // Backend handles this automatically via role-based filtering
                // Don't set assigned_to_id filter to allow backend OR logic to work
                setFilters(prev => ({ ...prev, assigned_to_id: '', status: '' }));
            } else {
                // Admins and superadmins see all (backend handles their filtering too)
                setFilters(prev => ({ ...prev, assigned_to_id: '', status: '' }));
            }
        }
    }, [user]);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    
    // State for subordinate users' assigned content sources (admins only)
    const [subordinateContentSources, setSubordinateContentSources] = useState<ContentSource[]>([]);
    
    // State for users list (for filters)
    const [users, setUsers] = useState<User[]>([]);
    
    // State for combined user filter
    const [userFilterType, setUserFilterType] = useState<'assigned_to' | 'created_by' | 'updated_by' | ''>('');
    const [selectedUserId, setSelectedUserId] = useState<string>('');

    // Queries
    const { 
        data: contentSourcesResponse, 
        isLoading, 
        error, 
        refetch 
    } = useContentSources(filters);

    const createContentSourceMutation = useCreateContentSource();
    const deleteContentSourceMutation = useDeleteContentSource();
    const assignContentSourceMutation = useAssignContentSource();
    const exportContentSourceMutation = useExportContentSource();
    const { mutateAsync: updateContentSource } = useUpdateContentSource();

    // Memoize filter values to prevent unnecessary re-fetches
    const filterSearch = filters.search || '';
    const filterStatus = filters.status || '';
    const filterVisibility = filters.visibility || '';
    const filterReviewStatus = filters.review_status || '';
    const filterTags = useMemo(() => filters.filter_tags || [], [filters.filter_tags]);
    const filterSortBy = filters.sortBy || 'updated_at';
    const filterSortOrder = filters.sortOrder || 'DESC';

    // Fetch users for filters
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const usersData = await getUsers();
                setUsers(usersData);
            } catch (err) {
                console.error('Error fetching users:', err);
                setUsers([]);
            }
        };
        fetchUsers();
    }, []);

    // Fetch content sources assigned to subordinate users (for admins only)
    // Superadmins see everything from the backend, so no need to fetch separately
    useEffect(() => {
        const fetchSubordinateData = async () => {
            if (user && user.role === 'admin') {
                try {
                    // Single backend call to get all content sources assigned to subordinate users
                    const response = await contentSourceApi.getSubordinateAssigned({
                        search: filterSearch,
                        status: filterStatus,
                        visibility: filterVisibility,
                        review_status: filterReviewStatus,
                        filter_tags: filterTags,
                        assigned_to_id: filters.assigned_to_id,
                        created_by_id: filters.created_by_id,
                        updated_by_id: filters.updated_by_id,
                        sortBy: filterSortBy,
                        sortOrder: filterSortOrder
                    });
                    
                    setSubordinateContentSources(response.data);
                } catch (err) {
                    console.error('Error fetching subordinate assigned content sources:', err);
                    setSubordinateContentSources([]);
                }
            } else {
                // Clear subordinate content sources if not admin
                setSubordinateContentSources([]);
            }
        };
        
        fetchSubordinateData();
    }, [user, user?.role, filterSearch, filterStatus, filterVisibility, filterReviewStatus, filterTags, filterSortBy, filterSortOrder, filters.assigned_to_id, filters.created_by_id, filters.updated_by_id]);

    // Combine main content sources with subordinate users' assigned content sources
    // Superadmins see everything from backend, so no need to combine
    const contentSources = useMemo(() => {
        const mainSources: ContentSource[] = contentSourcesResponse?.data || [];
        
        // For superadmins, backend already returns everything, so just use main sources
        if (user?.role === 'superadmin') {
            return mainSources;
        }
        
        // For admins, combine with subordinate content sources
        const allSources: ContentSource[] = [...mainSources];
        const mainSourceIds = new Set<string>(mainSources.map((cs: ContentSource) => cs.id));
        
        if (user?.role === 'admin') {
            for (const cs of subordinateContentSources) {
                if (!mainSourceIds.has(cs.id)) {
                    allSources.push(cs);
                }
            }
        }
        
        return allSources;
    }, [contentSourcesResponse?.data, subordinateContentSources, user]);
    
    const pagination = contentSourcesResponse?.pagination;

    // Filter handlers
    const handleFilterChange = (key: keyof FilterState, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1 // Reset to first page when filters change
        }));
    };

    // Handle user filter type change
    const handleUserFilterTypeChange = (type: 'assigned_to' | 'created_by' | 'updated_by' | '') => {
        setUserFilterType(type);
        // Clear all user filters when changing type
        setFilters(prev => ({
            ...prev,
            assigned_to_id: '',
            created_by_id: '',
            updated_by_id: '',
            page: 1
        }));
        setSelectedUserId('');
    };

    // Handle user selection change
    const handleUserSelectionChange = (userId: string) => {
        setSelectedUserId(userId);
        // Clear all user filters first
        const newFilters = {
            assigned_to_id: '',
            created_by_id: '',
            updated_by_id: ''
        };
        
        // Set the appropriate filter based on type
        if (userFilterType === 'assigned_to') {
            newFilters.assigned_to_id = userId;
        } else if (userFilterType === 'created_by') {
            newFilters.created_by_id = userId;
        } else if (userFilterType === 'updated_by') {
            newFilters.updated_by_id = userId;
        }
        
        setFilters(prev => ({
            ...prev,
            ...newFilters,
            page: 1
        }));
    };

    // Sync selectedUserId with current filter values when filters change externally (e.g., auto-filter for reviewers)
    useEffect(() => {
        // Check which filter is active
        if (filters.assigned_to_id) {
            if (userFilterType !== 'assigned_to' || selectedUserId !== filters.assigned_to_id) {
                setUserFilterType('assigned_to');
                setSelectedUserId(filters.assigned_to_id);
            }
        } else if (filters.created_by_id) {
            if (userFilterType !== 'created_by' || selectedUserId !== filters.created_by_id) {
                setUserFilterType('created_by');
                setSelectedUserId(filters.created_by_id);
            }
        } else if (filters.updated_by_id) {
            if (userFilterType !== 'updated_by' || selectedUserId !== filters.updated_by_id) {
                setUserFilterType('updated_by');
                setSelectedUserId(filters.updated_by_id);
            }
        } else if (!filters.assigned_to_id && !filters.created_by_id && !filters.updated_by_id) {
            // All user filters cleared, reset the UI only if we had something set
            if (userFilterType || selectedUserId) {
                setUserFilterType('');
                setSelectedUserId('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.assigned_to_id, filters.created_by_id, filters.updated_by_id]);

    const [searchInput, setSearchInput] = useState('');

    // Sync searchInput with filters.search when filters change externally
    useEffect(() => {
        setSearchInput(filters.search);
    }, [filters.search]);

    const debouncedSearch = useMemo(
        () => cmsUtils.debounce((search: string) => {
            handleFilterChange('search', search);
        }, 500),
        []
    );

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        debouncedSearch(value);
    };

    const handleSearchClear = () => {
        setSearchInput('');
        handleFilterChange('search', '');
    };

    const handleSort = (sortBy: string) => {
        setFilters(prev => {
            if (prev.sortBy === sortBy) {
                // Toggle sort order if same column
                return {
                    ...prev,
                    sortOrder: prev.sortOrder === 'ASC' ? 'DESC' : 'ASC'
                };
            } else {
                // New column, default to DESC
                return {
                    ...prev,
                    sortBy,
                    sortOrder: 'DESC'
                };
            }
        });
    };

    // Action handlers
    const [createError, setCreateError] = useState<string | null>(null);
    
    const handleCreateContentSource = async (data: any) => {
        setCreateError(null); // Clear previous errors
        try {
            const newContentSource = await createContentSourceMutation.mutateAsync(data);
            setShowCreateModal(false);
            refetch();
            // Navigate to the new content source editor
            if (newContentSource && newContentSource.id) {
                navigate(`/cms/editor/${newContentSource.id}`);
            }
        } catch (error: any) {
            console.error('Failed to create content source:', error);
            // Extract error message from various possible error formats
            const errorMessage = error?.response?.data?.message || 
                                error?.message || 
                                'Failed to create content source. Please try again.';
            setCreateError(errorMessage);
        }
    };
    
    const handleEdit = useCallback((id: string) => {
        // Navigate directly - fresh data will be fetched when editor mounts
        navigate(`/cms/editor/${id}`);
    }, [navigate]);

    const handleDeleteContentSource = async (contentSourceId: string) => {
        try {
            await deleteContentSourceMutation.mutateAsync(contentSourceId);
            setShowDeleteModal(null);
            refetch();
        } catch (error) {
            console.error('Failed to delete content source:', error);
        }
    };

    const handleExportContentSource = useCallback(async (contentSourceId: string) => {
        try {
            const exportData = await exportContentSourceMutation.mutateAsync(contentSourceId);
            // Download as JSON file
            const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `content-source-${contentSourceId}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export content source:', error);
        }
    }, [exportContentSourceMutation]);

    // Pagination handlers
    const handlePageChange = (page: number) => {
        setFilters(prev => ({ ...prev, page }));
    };

    // Status change handlers for admins/superadmins
    const handleStatusChange = useCallback(async (contentSourceId: string, newStatus: ContentStatus) => {
        try {
            await updateContentSource({
                id: contentSourceId,
                data: { status: newStatus }
            });
            refetch();
        } catch (error) {
            console.error('Failed to update status:', error);
        }
    }, [updateContentSource, refetch]);

    // Helper function to get permissions for a content source
    const getPermissions = useCallback((contentSource: ContentSource) => {
        return {
            role: user?.role || 'editor',
            canCreateContentSource: ['admin', 'superadmin', 'editor', 'editor-reviewer'].includes(user?.role || ''),
            canEditContentSource: ['admin', 'superadmin'].includes(user?.role || '') || 
                (['editor', 'editor-reviewer'].includes(user?.role || '') && 
                 (contentSource.assigned_to_id === user?.id || contentSource.created_by_id === user?.id)),
            canReviewContentSource: ['reviewer', 'editor-reviewer', 'admin', 'superadmin'].includes(user?.role || '') &&
                (contentSource.status === 'ready_for_review' || contentSource.status === 'ethics_review' || contentSource.status === 'ethics_review_in_process'),
            canDeleteContentSource: ['admin', 'superadmin'].includes(user?.role || ''),
            canAssignContentSource: ['admin', 'superadmin'].includes(user?.role || ''),
            canExportContentSource: ['admin', 'superadmin'].includes(user?.role || ''),
            canCreateSection: ['admin', 'superadmin'].includes(user?.role || '') || 
                (['editor', 'editor-reviewer'].includes(user?.role || '') && 
                 (contentSource.assigned_to_id === user?.id || contentSource.created_by_id === user?.id)),
            canEditSection: ['admin', 'superadmin'].includes(user?.role || '') || 
                (['editor', 'editor-reviewer'].includes(user?.role || '') && 
                 (contentSource.assigned_to_id === user?.id || contentSource.created_by_id === user?.id)),
            canReviewSection: ['reviewer', 'editor-reviewer', 'admin', 'superadmin'].includes(user?.role || '') &&
                (contentSource.status === 'ready_for_review' || contentSource.status === 'ethics_review' || contentSource.status === 'ethics_review_in_process'),
            canDeleteSection: ['admin', 'superadmin'].includes(user?.role || '') || 
                (['editor', 'editor-reviewer'].includes(user?.role || '') && 
                 (contentSource.assigned_to_id === user?.id || contentSource.created_by_id === user?.id)),
            canReorderSections: ['admin', 'superadmin'].includes(user?.role || '') || 
                (['editor', 'editor-reviewer'].includes(user?.role || '') && 
                 (contentSource.assigned_to_id === user?.id || contentSource.created_by_id === user?.id))
        };
    }, [user?.role, user?.id]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                            Error loading content sources
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                            {error.message || 'An unexpected error occurred'}
                        </div>
                        <div className="mt-4">
                            <button
                                onClick={() => refetch()}
                                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="cms-dashboard-container">
            {/* Header */}
            <div className="cms-dashboard-header">
                <div className="cms-dashboard-header-content">
                    <div style={{ flex: 1 }}>
                        <h1 className="cms-dashboard-title">
                            {user?.role === 'reviewer' ? 'Content Ready for Review' : 'Content Sources'}
                        </h1>
                        <p className="cms-dashboard-subtitle">
                            {user?.role === 'reviewer' 
                                ? 'Review and approve content sources assigned to you' 
                                : 'Manage and organize your content sources'}
                        </p>
                        {/* Status Flow Visualization for Admins and Superadmins */}
                        {(user?.role === 'admin' || user?.role === 'superadmin') && (
                            <div className="cms-status-flow-container">
                                <div className="cms-status-flow-label">Content Status Flow:</div>
                                <div className="cms-status-flow">
                                    {[
                                        'draft',
                                        'ready_for_review',
                                        'ethics_review',
                                        'ethics_review_in_process',
                                        'ethics_reviewed',
                                        'entity_extraction_in_process',
                                        'entity_reviewed',
                                        'published'
                                    ].map((status, index, array) => (
                                        <React.Fragment key={status}>
                                            <div className={`cms-status-flow-step cms-status-flow-step-${CONTENT_STATUS_COLORS[status as ContentStatus]}`}>
                                                <div className="cms-status-flow-step-badge">
                                                    {CONTENT_STATUS_LABELS[status as ContentStatus]}
                                                </div>
                                            </div>
                                            {index < array.length - 1 && (
                                                <div className="cms-status-flow-arrow">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    {user?.role !== 'reviewer' && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="cms-create-button"
                        >
                            Create Content Source
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="cms-filters-container">
                <div className="cms-filters">
                    <div className="cms-filters-grid">
                        {/* Search */}
                        <div className="cms-filter-group">
                            <label htmlFor="cms-search-input" className="cms-filter-label">Search</label>
                            <div className="cms-search-container">
                                <input
                                    id="cms-search-input"
                                    name="search"
                                    type="text"
                                    placeholder="Search by title or tags..."
                                    className="cms-filter-input"
                                    value={searchInput}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                />
                                {searchInput && (
                                    <button
                                        type="button"
                                        className="cms-search-clear"
                                        onClick={handleSearchClear}
                                        title="Clear search"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Status Filter - Hidden for reviewers since they only see ethics_review_in_process */}
                        {user?.role !== 'reviewer' && (
                            <div className="cms-filter-group">
                                <label htmlFor="cms-filter-status" className="cms-filter-label">Status</label>
                                <select
                                    id="cms-filter-status"
                                    name="status"
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    className="cms-filter-select"
                                >
                                    <option value="">All Statuses</option>
                                    {Object.entries(CONTENT_STATUS_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Visibility Filter */}
                        <div className="cms-filter-group">
                            <label htmlFor="cms-filter-visibility" className="cms-filter-label">Visibility</label>
                            <select
                                id="cms-filter-visibility"
                                name="visibility"
                                value={filters.visibility}
                                onChange={(e) => handleFilterChange('visibility', e.target.value)}
                                className="cms-filter-select"
                            >
                                <option value="">All Visibility</option>
                                {Object.entries(VISIBILITY_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Review Status Filter */}
                        <div className="cms-filter-group">
                            <label htmlFor="cms-filter-review-status" className="cms-filter-label">Review Status</label>
                            <select
                                id="cms-filter-review-status"
                                name="review_status"
                                value={filters.review_status}
                                onChange={(e) => handleFilterChange('review_status', e.target.value)}
                                className="cms-filter-select"
                            >
                                <option value="">All Review Status</option>
                                {Object.entries(REVIEW_STATUS_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Combined User Filter */}
                        <div className="cms-filter-group">
                            <label htmlFor="cms-filter-user-type" className="cms-filter-label">Filter By User</label>
                            <div style={{ display: 'flex', gap: '8px', width: '100%', minWidth: 0 }}>
                                <select
                                    id="cms-filter-user-type"
                                    name="user_filter_type"
                                    value={userFilterType}
                                    onChange={(e) => handleUserFilterTypeChange(e.target.value as 'assigned_to' | 'created_by' | 'updated_by' | '')}
                                    className="cms-filter-select"
                                    style={{ flex: '0 0 auto', minWidth: '140px', maxWidth: '50%', width: 'auto' }}
                                >
                                    <option value="">Select Type</option>
                                    <option value="assigned_to">Assigned To</option>
                                    <option value="created_by">Created By</option>
                                    <option value="updated_by">Updated By</option>
                                </select>
                                <select
                                    id="cms-filter-user-id"
                                    name="user_id"
                                    value={selectedUserId}
                                    onChange={(e) => handleUserSelectionChange(e.target.value)}
                                    className="cms-filter-select"
                                    disabled={!userFilterType}
                                    style={{ flex: 1, minWidth: 0, width: '100%' }}
                                >
                                    <option value="">All Users</option>
                                    {users.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.size > 0 && (user?.role === 'admin' || user?.role === 'superadmin') && (
                <div className="cms-bulk-action-bar">
                    <div className="cms-bulk-action-info">
                        <span>{selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected</span>
                    </div>
                    <div className="cms-bulk-action-buttons">
                        <button
                            onClick={() => setShowBulkAssignModal(true)}
                            className="cms-bulk-action-button"
                        >
                            Assign for Review
                        </button>
                        <button
                            onClick={() => setSelectedIds(new Set())}
                            className="cms-bulk-action-button cms-bulk-action-button-cancel"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>
            )}

            {/* Content Sources Table */}
            <div className="cms-content-grid">
                <ContentSourceTable
                    contentSources={contentSources}
                    filters={filters}
                    onSort={handleSort}
                    onEdit={handleEdit}
                    onAssign={(id) => setShowAssignModal(id)}
                    onDelete={(id) => setShowDeleteModal(id)}
                    onExport={handleExportContentSource}
                    onStatusChange={handleStatusChange}
                    getPermissions={getPermissions}
                    selectedIds={selectedIds}
                    onSelectionChange={setSelectedIds}
                    canSelect={(cs) => (user?.role === 'admin' || user?.role === 'superadmin') && 
                                      (cs.status === 'ethics_review')}
                    userRole={user?.role}
                />

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                    <div className="cms-pagination">
                        <nav className="cms-pagination-nav">
                            <button
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={!pagination.hasPreviousPage}
                                className="cms-pagination-button"
                            >
                                Previous
                            </button>
                            
                            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    onClick={() => handlePageChange(page)}
                                    className={`cms-pagination-button ${
                                        page === pagination.currentPage ? 'cms-pagination-button-active' : ''
                                    }`}
                                >
                                    {page}
                                </button>
                            ))}
                            
                            <button
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={!pagination.hasNextPage}
                                className="cms-pagination-button"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                )}
            </div>

            {/* Modals */}
            {showCreateModal && (
                <ContentSourceModal
                    mode="create"
                    onClose={() => {
                        setShowCreateModal(false);
                        setCreateError(null); // Clear error when closing modal
                    }}
                    onSubmit={handleCreateContentSource}
                    error={createError}
                    isSubmitting={createContentSourceMutation.isPending}
                />
            )}

            {showAssignModal && (
                <AssignContentSourceModal
                    contentSourceId={showAssignModal}
                    onClose={() => setShowAssignModal(null)}
                    onAssignComplete={() => {
                        setShowAssignModal(null);
                        refetch();
                    }}
                    assignMutation={assignContentSourceMutation}
                />
            )}

            {showDeleteModal && (
                <DeleteContentSourceModal
                    contentSourceId={showDeleteModal}
                    onClose={() => setShowDeleteModal(null)}
                    onConfirm={handleDeleteContentSource}
                />
            )}

            {showBulkAssignModal && (
                <BulkAssignForReviewModal
                    contentSourceIds={Array.from(selectedIds)}
                    onClose={() => {
                        setShowBulkAssignModal(false);
                        setSelectedIds(new Set());
                    }}
                    onAssignComplete={() => {
                        setShowBulkAssignModal(false);
                        setSelectedIds(new Set());
                        refetch();
                    }}
                />
            )}
        </div>
    );
};

// Content Source Table Component
const ContentSourceTable: React.FC<{
    contentSources: ContentSource[];
    filters: FilterState;
    onSort: (sortBy: string) => void;
    onEdit: (id: string) => void;
    onAssign: (id: string) => void;
    onDelete: (id: string) => void;
    onExport: (id: string) => void;
    onStatusChange?: (id: string, status: ContentStatus) => void;
    getPermissions: (contentSource: ContentSource) => any;
    selectedIds: Set<string>;
    onSelectionChange: (ids: Set<string>) => void;
    canSelect: (contentSource: ContentSource) => boolean;
    userRole?: string;
}> = ({ contentSources, filters, onSort, onEdit, onAssign, onDelete, onExport, onStatusChange, getPermissions, selectedIds, onSelectionChange, canSelect, userRole }) => {
    const [openStatusDropdown, setOpenStatusDropdown] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
    const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const buttonRefs = useRef<Record<string, HTMLSpanElement | null>>({});

    // Calculate dropdown position with overflow detection
    const calculateDropdownPosition = useCallback((button: HTMLElement) => {
        const rect = button.getBoundingClientRect();
        const dropdownHeight = 300; // max-height from CSS
        const dropdownWidth = 200; // min-width from CSS
        const spacing = 4;
        
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        
        let top: number;
        let left: number;
        
        // Check if dropdown would overflow at bottom
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            // Position above the button
            top = rect.top - dropdownHeight - spacing;
        } else {
            // Position below the button
            top = rect.bottom + spacing;
        }
        
        // Ensure dropdown doesn't go above viewport
        if (top < 0) {
            top = spacing;
        }
        
        // Ensure dropdown doesn't go below viewport
        // But only adjust if it won't result in a negative value
        if (top + dropdownHeight > viewportHeight) {
            const adjustedTop = viewportHeight - dropdownHeight - spacing;
            // Only apply the adjustment if it doesn't make top negative
            // If viewport is too small, keep it at the top (spacing)
            top = Math.max(adjustedTop, spacing);
        }
        
        // Handle horizontal positioning
        left = rect.left;
        
        // Check if dropdown would overflow on the right
        if (left + dropdownWidth > viewportWidth) {
            left = viewportWidth - dropdownWidth - spacing;
        }
        
        // Ensure dropdown doesn't go left of viewport
        if (left < 0) {
            left = spacing;
        }
        
        return { top, left };
    }, []);

    // Update dropdown position on scroll
    useEffect(() => {
        const handleScroll = () => {
            if (openStatusDropdown) {
                const button = buttonRefs.current[openStatusDropdown];
                if (button) {
                    const newPosition = calculateDropdownPosition(button);
                    setDropdownPosition(newPosition);
                }
            }
        };

        const handleResize = () => {
            if (openStatusDropdown) {
                const button = buttonRefs.current[openStatusDropdown];
                if (button) {
                    const newPosition = calculateDropdownPosition(button);
                    setDropdownPosition(newPosition);
                }
            }
        };

        if (openStatusDropdown) {
            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleResize);
        }

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [openStatusDropdown, calculateDropdownPosition]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openStatusDropdown) {
                const dropdownElement = dropdownRefs.current[openStatusDropdown];
                const buttonElement = buttonRefs.current[openStatusDropdown];
                if (dropdownElement && !dropdownElement.contains(event.target as Node) &&
                    buttonElement && !buttonElement.contains(event.target as Node)) {
                    setOpenStatusDropdown(null);
                    setDropdownPosition(null);
                }
            }
        };

        if (openStatusDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openStatusDropdown]);

    const getSortIcon = (column: string) => {
        if (filters.sortBy !== column) return null;
        return filters.sortOrder === 'ASC' ? '↑' : '↓';
    };

    const handleRowClick = (id: string) => {
        onEdit(id);
    };

    const selectableContentSources = contentSources.filter(cs => canSelect(cs));
    const allSelectableSelected = selectableContentSources.length > 0 && 
                                  selectableContentSources.every(cs => selectedIds.has(cs.id));
    const someSelectableSelected = selectableContentSources.some(cs => selectedIds.has(cs.id));

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (allSelectableSelected) {
            onSelectionChange(new Set());
        } else {
            const newSelected = new Set(selectedIds);
            selectableContentSources.forEach(cs => newSelected.add(cs.id));
            onSelectionChange(newSelected);
        }
    };

    const handleSelectOne = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
        e.stopPropagation();
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        onSelectionChange(newSelected);
    };

    if (contentSources.length === 0) {
        return (
            <div className="cms-empty-state">
                <div className="cms-empty-state-icon">📄</div>
                <h3 className="cms-empty-state-title">No content sources found</h3>
                <p className="cms-empty-state-description">
                    {filters.search || filters.status || filters.visibility || filters.review_status
                        ? 'Try adjusting your filters'
                        : 'Create your first content source to get started'}
                </p>
            </div>
        );
    }

    return (
        <div className="cms-table-container">
            <table className="cms-table">
                <thead>
                    <tr>
                        <th 
                            className="cms-table-header"
                            style={{ width: '40px', padding: '8px' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <input
                                id="cms-select-all"
                                name="select_all"
                                type="checkbox"
                                checked={allSelectableSelected}
                                ref={(input) => {
                                    if (input) input.indeterminate = someSelectableSelected && !allSelectableSelected;
                                }}
                                onChange={handleSelectAll}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                            />
                        </th>
                        <th 
                            className="cms-table-header cms-table-header-sortable"
                            onClick={() => onSort('title')}
                        >
                            Title {getSortIcon('title')}
                        </th>
                        <th 
                            className="cms-table-header cms-table-header-sortable"
                            onClick={() => onSort('status')}
                        >
                            Status {getSortIcon('status')}
                        </th>
                        <th className="cms-table-header">
                            Visibility
                        </th>
                        <th className="cms-table-header">
                            Source
                        </th>
                        <th 
                            className="cms-table-header cms-table-header-sortable"
                            onClick={() => onSort('created_at')}
                        >
                            Created {getSortIcon('created_at')}
                        </th>
                        <th 
                            className="cms-table-header cms-table-header-sortable"
                            onClick={() => onSort('updated_at')}
                        >
                            Updated {getSortIcon('updated_at')}
                        </th>
                        <th className="cms-table-header">
                            Assigned To
                        </th>
                        <th className="cms-table-header cms-table-header-actions">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {contentSources.map((contentSource) => {
                        const permissions = getPermissions(contentSource);
                        const statusColor = CONTENT_STATUS_COLORS[contentSource.status];
                        const reviewStatusColor = REVIEW_STATUS_COLORS[contentSource.review_status];

                        const isSelectable = canSelect(contentSource);
                        const isSelected = selectedIds.has(contentSource.id);

                        return (
                            <tr 
                                key={contentSource.id}
                                className="cms-table-row"
                                onClick={() => handleRowClick(contentSource.id)}
                            >
                                <td 
                                    className="cms-table-cell"
                                    style={{ width: '40px', padding: '8px' }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {isSelectable && (
                                        <input
                                            id={`cms-select-${contentSource.id}`}
                                            name={`select_${contentSource.id}`}
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => handleSelectOne(e, contentSource.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        />
                                    )}
                                </td>
                                <td className="cms-table-cell cms-table-cell-title">
                                    <div className="cms-table-title">
                                        {contentSource.title}
                                        {contentSource.section_count !== undefined && contentSource.section_count > 0 && (
                                            <span className="cms-table-sections-count">
                                                ({contentSource.section_count} {contentSource.section_count === 1 ? 'section' : 'sections'})
                                            </span>
                                        )}
                                    </div>
                                    {contentSource.filter_tags.length > 0 && (
                                        <div className="cms-table-tags">
                                            {contentSource.filter_tags.slice(0, 3).map((tag, index) => (
                                                <span key={index} className="cms-tag">
                                                    {tag}
                                                </span>
                                            ))}
                                            {contentSource.filter_tags.length > 3 && (
                                                <span className="cms-tag cms-tag-more">
                                                    +{contentSource.filter_tags.length - 3}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td className="cms-table-cell">
                                    <div className="cms-table-badges">
                                        {userRole === 'superadmin' && onStatusChange ? (
                                            <>
                                                <div 
                                                    className="cms-status-dropdown-container"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <span 
                                                        ref={(el) => {
                                                            buttonRefs.current[contentSource.id] = el;
                                                        }}
                                                        className={`cms-badge cms-badge-${statusColor || 'gray'} cms-badge-clickable`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const button = e.currentTarget;
                                                            if (openStatusDropdown === contentSource.id) {
                                                                setOpenStatusDropdown(null);
                                                                setDropdownPosition(null);
                                                            } else {
                                                                setOpenStatusDropdown(contentSource.id);
                                                                const position = calculateDropdownPosition(button);
                                                                setDropdownPosition(position);
                                                            }
                                                        }}
                                                    >
                                                        {CONTENT_STATUS_LABELS[contentSource.status]}
                                                        <span className="cms-badge-arrow">▼</span>
                                                    </span>
                                                </div>
                                                {openStatusDropdown === contentSource.id && dropdownPosition && (
                                                    <div 
                                                        className="cms-status-dropdown"
                                                        ref={(el) => {
                                                            dropdownRefs.current[contentSource.id] = el;
                                                        }}
                                                        style={{
                                                            top: `${dropdownPosition.top}px`,
                                                            left: `${dropdownPosition.left}px`
                                                        }}
                                                    >
                                                        {Object.entries(CONTENT_STATUS_LABELS).map(([status, label]) => (
                                                            <div
                                                                key={status}
                                                                className={`cms-status-dropdown-item ${
                                                                    contentSource.status === status ? 'cms-status-dropdown-item-active' : ''
                                                                }`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (contentSource.status !== status) {
                                                                        onStatusChange(contentSource.id, status as ContentStatus);
                                                                    }
                                                                    setOpenStatusDropdown(null);
                                                                    setDropdownPosition(null);
                                                                }}
                                                            >
                                                                {label}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <span className={`cms-badge cms-badge-${statusColor || 'gray'}`}>
                                                {CONTENT_STATUS_LABELS[contentSource.status]}
                                            </span>
                                        )}
                                        {contentSource.review_status !== 'none' && (
                                            <span className={`cms-badge cms-badge-${reviewStatusColor === 'red' ? 'red' : 'green'}`}>
                                                {REVIEW_STATUS_LABELS[contentSource.review_status]}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="cms-table-cell">
                                    <span className={`cms-badge cms-badge-${VISIBILITY_COLORS[contentSource.visibility] || 'gray'}`}>
                                        {VISIBILITY_LABELS[contentSource.visibility]}
                                    </span>
                                </td>
                                <td className="cms-table-cell">
                                    {SOURCE_LABELS[contentSource.source] || contentSource.source}
                                </td>
                                <td className="cms-table-cell">
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                            {contentSource.created_by?.name || 'Unknown'}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                            {cmsUtils.formatDate(contentSource.created_at)}
                                        </div>
                                    </div>
                                </td>
                                <td className="cms-table-cell">
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                            {contentSource.updated_by?.name || '-'}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                            {cmsUtils.formatDate(contentSource.updated_at)}
                                        </div>
                                    </div>
                                </td>
                                <td className="cms-table-cell">
                                    {contentSource.assigned_to?.name || 'Unassigned'}
                                </td>
                                <td 
                                    className="cms-table-cell cms-table-cell-actions"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="cms-table-actions">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEdit(contentSource.id);
                                            }}
                                            disabled={!permissions.canEditContentSource}
                                            className="cms-table-action-button cms-table-action-button-primary"
                                            title={permissions.canEditContentSource ? "Edit content source" : "You don't have permission to edit this content source"}
                                        >
                                            Edit
                                        </button>
                                        {permissions.canAssignContentSource && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onAssign(contentSource.id);
                                                }}
                                                className="cms-table-action-button cms-table-action-button-assign"
                                                title="Assign content source to a user"
                                            >
                                                Assign
                                            </button>
                                        )}
                                        {/* {permissions.canExportContentSource && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onExport(contentSource.id);
                                                }}
                                                className="cms-table-action-button cms-table-action-button-export"
                                                title="Export content source for AI ingestion"
                                            >
                                                Export
                                            </button>
                                        )} */}
                                        {/* Status change buttons for admins/superadmins */}
                                        {onStatusChange && (userRole === 'admin' || userRole === 'superadmin') && (
                                            <>
                                                {contentSource.status === 'ready_for_review' && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onStatusChange(contentSource.id, 'ethics_review');
                                                        }}
                                                        className="cms-table-action-button cms-table-action-button-send"
                                                        title="Send for Ethics Review"
                                                    >
                                                        <SendHorizontal size={16} />
                                                        Ethics Review
                                                    </button>
                                                )}
                                                {contentSource.status === 'ethics_reviewed' && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onStatusChange(contentSource.id, 'entity_extraction_in_process');
                                                        }}
                                                        className="cms-table-action-button cms-table-action-button-send"
                                                        title="Start Entity Extraction"
                                                    >
                                                        <SendHorizontal size={16} />
                                                        Start Extraction
                                                    </button>
                                                )}
                                            </>
                                        )}
                                        {permissions.canDeleteContentSource && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDelete(contentSource.id);
                                                }}
                                                className="cms-table-action-button cms-table-action-button-delete"
                                                title="Delete content source"
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

// Modal Components
const AssignContentSourceModal: React.FC<{
    contentSourceId: string;
    onClose: () => void;
    onAssignComplete: () => void;
    assignMutation: ReturnType<typeof useAssignContentSource>;
}> = ({ contentSourceId, onClose, onAssignComplete, assignMutation }) => {
    const { data: contentSource } = useContentSource(contentSourceId);
    const [subordinates, setSubordinates] = useState<User[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [userError, setUserError] = useState<string>('');

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoadingUsers(true);
                setUserError('');
                const users = await getSubordinateUsers();
                setSubordinates(users);
                // Pre-select current assignee if available
                if (contentSource?.assigned_to_id) {
                    setSelectedUserId(contentSource.assigned_to_id);
                }
            } catch (err) {
                setUserError('Failed to load users');
                console.error('Error fetching subordinate users:', err);
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchUsers();
    }, [contentSource?.assigned_to_id]);

    const handleSubmit = async () => {
        if (!selectedUserId) {
            return;
        }

        try {
            await assignMutation.mutateAsync({
                id: contentSourceId,
                data: { userId: selectedUserId }
            });
            onAssignComplete();
        } catch (err: any) {
            // Error is handled by the mutation hook, but we can show it
            console.error('Error assigning content source:', err);
        }
    };

    const isLoading = loadingUsers || assignMutation.isPending;
    const error = userError || (assignMutation.error as any)?.response?.data?.message || (assignMutation.error as any)?.message || '';

    const handleClose = () => {
        setUserError('');
        setSelectedUserId('');
        onClose();
    };

    return (
        <div className="cms-modal-overlay" onClick={handleClose}>
            <div className="cms-modal" onClick={(e) => e.stopPropagation()} style={{ width: '500px' }}>
                <h2 className="cms-modal-title">Assign Content Source</h2>
                <div className="cms-modal-form">
                    <div className="cms-modal-form-group">
                        <div style={{ 
                            padding: '12px', 
                            backgroundColor: '#f3f4f6', 
                            borderRadius: '6px', 
                            borderLeft: '4px solid #3b82f6',
                            marginBottom: '1rem'
                        }}>
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                                {contentSource?.title || 'Loading...'}
                            </div>
                            {contentSource?.assigned_to && (
                                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                    Currently assigned to: <strong>{contentSource.assigned_to.name}</strong> ({contentSource.assigned_to.role})
                                </div>
                            )}
                        </div>
                    </div>

                    {(error || userError) && (
                        <div className="cms-modal-form-group" style={{
                            padding: '0.75rem 1rem',
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            color: '#dc2626',
                            fontSize: '0.875rem',
                            marginBottom: '1rem'
                        }}>
                            {error || userError}
                        </div>
                    )}

                    <div className="cms-modal-form-group">
                        <label htmlFor="assign-user-select" className="cms-modal-label">
                            Assign to:
                        </label>
                        <select
                            id="assign-user-select"
                            name="assign_user"
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            disabled={isLoading}
                            className="cms-modal-select"
                        >
                            <option value="">Select a user...</option>
                            {subordinates.map((user) => (
                                <option key={user.id} value={user.id}>
                                    {user.name} ({user.role}) - {user.email}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="cms-modal-actions">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isLoading}
                            className="cms-modal-button cms-modal-button-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isLoading || !selectedUserId}
                            className="cms-modal-button cms-modal-button-submit"
                        >
                            {assignMutation.isPending ? 'Assigning...' : 'Assign'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DeleteContentSourceModal: React.FC<{
    contentSourceId: string;
    onClose: () => void;
    onConfirm: (contentSourceId: string) => void;
}> = ({ contentSourceId, onClose, onConfirm }) => {
    const { data: contentSource, isLoading } = useContentSource(contentSourceId);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleConfirm = async () => {
        setIsDeleting(true);
        try {
            await onConfirm(contentSourceId);
        } catch (error) {
            console.error('Error deleting content source:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="cms-modal-overlay" onClick={onClose}>
            <div className="cms-modal" onClick={(e) => e.stopPropagation()} style={{ width: '500px' }}>
                <h2 className="cms-modal-title">Delete Content Source</h2>
                <div className="cms-modal-form">
                    {isLoading ? (
                        <div className="cms-modal-form-group" style={{ textAlign: 'center', padding: '2rem' }}>
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                            <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading...</p>
                        </div>
                    ) : (
                        <>
                            <div className="cms-modal-form-group">
                                <div style={{ 
                                    padding: '12px', 
                                    backgroundColor: '#fef2f2', 
                                    borderRadius: '6px', 
                                    borderLeft: '4px solid #ef4444',
                                    marginBottom: '1rem'
                                }}>
                                    <div style={{ fontWeight: 600, marginBottom: '4px', color: '#dc2626' }}>
                                        ⚠️ Warning: This action cannot be undone
                                    </div>
                                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '8px' }}>
                                        You are about to delete:
                                    </div>
                                    <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937', marginTop: '4px' }}>
                                        {contentSource?.title || 'Loading...'}
                                    </div>
                                    {contentSource?.section_count !== undefined && contentSource.section_count > 0 && (
                                        <div style={{ fontSize: '0.875rem', color: '#ef4444', marginTop: '8px' }}>
                                            This content source contains {contentSource.section_count} {contentSource.section_count === 1 ? 'section' : 'sections'}. All sections will also be deleted.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="cms-modal-form-group">
                                <p style={{ color: '#6b7280', fontSize: '0.875rem', lineHeight: '1.5' }}>
                                    Are you sure you want to delete this content source? This will permanently remove all associated data including sections and cannot be recovered.
                                </p>
                            </div>

                            <div className="cms-modal-actions">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={isDeleting}
                                    className="cms-modal-button cms-modal-button-cancel"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirm}
                                    disabled={isDeleting}
                                    className="cms-modal-button"
                                    style={{
                                        backgroundColor: '#ef4444',
                                        color: 'white',
                                        border: 'none'
                                    }}
                                    onMouseEnter={(e) => !isDeleting && (e.currentTarget.style.backgroundColor = '#dc2626')}
                                    onMouseLeave={(e) => !isDeleting && (e.currentTarget.style.backgroundColor = '#ef4444')}
                                >
                                    {isDeleting ? 'Deleting...' : 'Delete Content Source'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const BulkAssignForReviewModal: React.FC<{
    contentSourceIds: string[];
    onClose: () => void;
    onAssignComplete: () => void;
}> = ({ contentSourceIds, onClose, onAssignComplete }) => {
    const [subordinates, setSubordinates] = useState<User[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [userError, setUserError] = useState<string>('');
    const [isAssigning, setIsAssigning] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoadingUsers(true);
                setUserError('');
                const users = await getSubordinateUsers();
                // Filter to only show users with roles that can be assigned for review
                const allowedRoles = ['reviewer', 'editor-reviewer', 'admin', 'superadmin'];
                const filteredUsers = users.filter((user: User) => allowedRoles.includes(user.role));
                setSubordinates(filteredUsers);
            } catch (err) {
                setUserError('Failed to load users');
                console.error('Error fetching subordinate users:', err);
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchUsers();
    }, []);

    const handleSubmit = async () => {
        if (!selectedUserId) {
            return;
        }

        try {
            setIsAssigning(true);
            setUserError('');
            await contentSourceApi.bulkAssignForReview(contentSourceIds, selectedUserId);
            onAssignComplete();
        } catch (err: any) {
            const errorMessage = err?.response?.data?.message || 
                                err?.message || 
                                'Failed to assign content sources for review';
            setUserError(errorMessage);
            console.error('Error assigning content sources for review:', err);
        } finally {
            setIsAssigning(false);
        }
    };

    const isLoading = loadingUsers || isAssigning;
    const error = userError;

    const handleClose = () => {
        setUserError('');
        setSelectedUserId('');
        onClose();
    };

    return (
        <div className="cms-modal-overlay" onClick={handleClose}>
            <div className="cms-modal" onClick={(e) => e.stopPropagation()} style={{ width: '500px' }}>
                <h2 className="cms-modal-title">Assign Content Sources for Review</h2>
                <div className="cms-modal-form">
                    <div className="cms-modal-form-group">
                        <div style={{ 
                            padding: '12px', 
                            backgroundColor: '#f3f4f6', 
                            borderRadius: '6px', 
                            borderLeft: '4px solid #3b82f6',
                            marginBottom: '1rem'
                        }}>
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                                {contentSourceIds.length} content source{contentSourceIds.length !== 1 ? 's' : ''} selected
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                These content sources will be assigned for review and their status will be changed to "Review in Process".
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="cms-modal-form-group" style={{
                            padding: '0.75rem 1rem',
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            color: '#dc2626',
                            fontSize: '0.875rem',
                            marginBottom: '1rem'
                        }}>
                            {error}
                        </div>
                    )}

                    <div className="cms-modal-form-group">
                        <label htmlFor="bulk-assign-user-select" className="cms-modal-label">
                            Assign to:
                        </label>
                        <select
                            id="bulk-assign-user-select"
                            name="bulk_assign_user"
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            disabled={isLoading}
                            className="cms-modal-select"
                        >
                            <option value="">Select a user...</option>
                            {subordinates.map((user) => (
                                <option key={user.id} value={user.id}>
                                    {user.name} ({user.role}) - {user.email}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="cms-modal-actions">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isLoading}
                            className="cms-modal-button cms-modal-button-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isLoading || !selectedUserId}
                            className="cms-modal-button cms-modal-button-submit"
                        >
                            {isAssigning ? 'Assigning...' : 'Assign for Review'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CMSDashboard;
